import type { ObserverTargetKind } from './observerTypes';

/**
 * The inventory's one ordering rule (`multi-observer-modal`, FR19), shared by both list views and
 * both panels so they can never sort differently.
 *
 * "Type" is the row's kind: the floor first, then zones (surfaces), then machines (items). The
 * design's Cardio / Strength split is not available — register rows carry no catalogue category
 * (`packages/frontend-types/src/twin-planner/Equipment.ts`, "absent for register rows") — so Rob
 * ruled on 2026-10-01 for the split the plan knows for certain.
 */
export type InventorySortKey = 'type' | 'name' | 'bid';
export type SortDirection = 'asc' | 'desc';

export interface SortableRow {
  name: string;
  bid?: string | null;
  kind: ObserverTargetKind;
}

const KIND_ORDER: Record<ObserverTargetKind, number> = { floor: 0, surface: 1, item: 2 };

/** The sub-header a type group shows. */
export const typeHeading = (kind: ObserverTargetKind): string =>
  kind === 'surface' ? 'ZONES' : kind === 'item' ? 'MACHINES' : 'FLOOR';

/* One collator each, built once: `localeCompare` with options builds one per call, and the list
   re-sorts on every render of the panel (review). */
export const NAME_COLLATOR = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
const BID_COLLATOR = new Intl.Collator(undefined, { numeric: true });

const byName = (a: SortableRow, b: SortableRow) => NAME_COLLATOR.compare(a.name, b.name);

export const sortInventoryRows = <T extends SortableRow>(
  rows: readonly T[],
  key: InventorySortKey = 'type',
  direction: SortDirection = 'asc',
): T[] => {
  const sign = direction === 'asc' ? 1 : -1;
  const compare = (a: T, b: T): number => {
    if (key === 'bid') {
      return sign * BID_COLLATOR.compare(a.bid ?? '', b.bid ?? '');
    }
    if (key === 'name') return sign * byName(a, b);
    // Reversed, the types reverse too — machines before zones — not only the names within them.
    return sign * (KIND_ORDER[a.kind] - KIND_ORDER[b.kind]) || sign * byName(a, b);
  };
  return [...rows].sort(compare);
};
